const PLACEHOLDER_SUMMARIES = new Set([
  'Healthcare conversation',
  'Healthcare Conversation',
]);

export const formatMeetingHost = (meetingUrl: any) => {
  try {
    return new URL(meetingUrl).hostname.replace(/^www\./, '');
  } catch {
    return meetingUrl || 'Unavailable';
  }
};

const firstPresent = (...values: any[]) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const collectConversationIds = (session: any, encounterContext: any) => {
  const ids = [
    ...(encounterContext?.conversation_ids || []),
    ...(encounterContext?.conversations || [])
      .map((conversation: any) => conversation?.id)
      .filter(Boolean),
    ...(session?.conversation_ids || []),
    ...(session?.conversations || [])
      .map((conversation: any) => conversation?.id)
      .filter(Boolean),
    session?.id,
  ].filter(Boolean);
  return Array.from(new Set(ids));
};

const findMemberValue = (encounterContext: any, fieldNames: any) => {
  const conversations = encounterContext?.conversations || [];
  for (const conversation of conversations) {
    for (const field of fieldNames) {
      const value = conversation?.[field];
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return null;
};

const buildTranscriptLabel = (count: any) => {
  if (typeof count !== 'number') return null;
  return count > 0 ? `${count} messages saved` : 'No transcript messages saved';
};

export const buildSessionOverviewItems = ({
  session,
  conversationId,
  messages = null,
  encounterContext = null,
  meetingUrl = '',
  botStatus = '',
}: Record<string, any> = {}) => {
  if (!session) return [];

  const context = encounterContext || session.__encounterContext || null;
  const conversationIds = collectConversationIds(session, context);
  const isRealEncounter = Boolean(context?.id && context.isSynthetic === false);
  const botId = firstPresent(
    session.bot_id,
    session.botId,
    findMemberValue(context, ['bot_id', 'botId']),
    context?.bot_id,
    context?.botId,
  );
  const meeting = firstPresent(
    meetingUrl,
    session.meeting_url,
    session.meetingUrl,
    findMemberValue(context, ['meeting_url', 'meetingUrl']),
    context?.meeting_url,
    context?.meetingUrl,
  );
  const resolvedBotStatus = firstPresent(
    botStatus,
    session.bot_status,
    session.botStatus,
    session.status_label,
    session.statusLabel,
  );
  const transcriptCount = Array.isArray(messages)
    ? messages.length
    : firstPresent(session.message_count, session.messageCount);
  const transcriptLabel = buildTranscriptLabel(transcriptCount);

  const items: any[] = [];

  if (isRealEncounter) {
    items.push({
      key: 'encounter-id',
      label: 'Encounter ID',
      value: context.id,
    });
  }

  if (conversationIds.length > 1) {
    items.push({
      key: 'conversation-ids',
      label: 'Conversations',
      value: `${conversationIds.length} conversations`,
      helper: conversationIds.join(', '),
    });
  } else {
    items.push({
      key: 'conversation-id',
      label: 'Conversation ID',
      value: conversationIds[0] || conversationId || 'Unavailable',
    });
  }

  if (botId) {
    items.push({
      key: 'bot-id',
      label: 'Session agent ID',
      value: botId,
    });
  }

  if (resolvedBotStatus) {
    items.push({
      key: 'bot-status',
      label: 'Session status',
      value: resolvedBotStatus,
    });
  }

  if (meeting) {
    items.push({
      key: 'meeting',
      label: 'Meeting',
      value: formatMeetingHost(meeting),
      helper: meeting,
    });
  }

  if (transcriptLabel) {
    items.push({
      key: 'transcript-count',
      label: 'Transcript',
      value: transcriptLabel,
    });
  }

  return items;
};

export const buildScheduledOverviewItems = (bot: any) => {
  if (!bot) return [];
  const meeting = firstPresent(bot.meeting_url, bot.meetingUrl);
  const items: any[] = [
    {
      key: 'bot-id',
      label: 'Session agent ID',
      value: bot.id || 'Unavailable',
    },
  ];

  if (bot.status) {
    items.push({
      key: 'bot-status',
      label: 'Session status',
      value: String(bot.status)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    });
  }

  if (meeting) {
    items.push({
      key: 'meeting',
      label: 'Meeting',
      value: formatMeetingHost(meeting),
      helper: meeting,
    });
  }

  if (bot.telecall_id) {
    items.push({
      key: 'conversation-id',
      label: 'Conversation ID',
      value: bot.telecall_id,
    });
  }

  if (typeof bot.transcriptCount === 'number') {
    items.push({
      key: 'transcript-count',
      label: 'Transcript',
      value: buildTranscriptLabel(bot.transcriptCount),
    });
  }

  return items.filter(item => !PLACEHOLDER_SUMMARIES.has(item.value));
};
